import { cva } from 'class-variance-authority';

/**
 * Button class recipe.
 *
 * Deliberately contains ZERO `dark:` variants. Colour comes entirely from the `--ds-btn-*` slot
 * variables, which the `.ds-btn-<appearance>-<fill>` classes in `components/styles/design-system.css`
 * set per theme. One class list therefore serves both themes.
 *
 * Sizing is height-based rather than padding-based on purpose: Figma gives each size a fixed height
 * (32/36/40/44), and `fill="outline"` adds a border. With padding-based sizing that border would
 * make outline buttons 2-3px taller than their solid siblings; with a fixed height plus the app's
 * global `box-sizing: border-box` they line up exactly.
 *
 * `2xs` (24px) sits BELOW the Figma scale and is the one size the set does not draw. It exists for
 * the Alert's dismiss control (HNS-339 — node 639:77378 draws a 24px × on a 20px glyph), and it is
 * icon-only: `IconButtonSize` is what admits it, so a 24px button with a label is unconstructable.
 * Adding a step was chosen over moving `xs` down, because `xs` is 32px and that is the height the
 * three legacy mappers in `common/components/Buttons` match MUI's own 32px buttons against. Moving
 * it would have taken 32 out of the scale entirely and dropped the refresh from every default-size
 * legacy button. See `Buttons/Button/toDsProps`.
 */
export const buttonRecipe = cva(
  [
    'group/btn',
    'relative inline-flex shrink-0 items-center justify-center',
    'rounded-full border-solid',
    'font-sans leading-5 font-medium whitespace-nowrap',
    'cursor-pointer select-none',
    'transition-colors duration-150 motion-reduce:transition-none',

    // Slot reads. The `.ds-btn-*` class supplies the values.
    'border-[length:var(--ds-btn-border-width)] border-[var(--ds-btn-border)]',
    'bg-[var(--ds-btn-bg)] text-[var(--ds-btn-fg)]',

    // Interactive states, and the three guards that keep all four of them mutually exclusive.
    //
    // `:not([data-disabled])` separates them from the disabled rules below, so a disabled button
    // never has to out-specify a hover or a pressed one. `:not(:active)` separates hover from
    // pressed, which matters because a MOUSE press matches `:hover` and `:active` at once: without
    // it both rules apply, they compile to the same specificity, and the winner is decided by
    // whatever order Tailwind happens to emit its utilities in. With it, exactly one of these four
    // can match any one interaction, so the emitted order stops being able to change what renders.
    //
    // The pressed pair is therefore what BOTH press paths draw — a mouse press and a space or enter
    // press now look alike, where before a mouse press showed the hover pair and only a keyboard
    // press reached the pressed fill. That fill is also why the pressed label exists: the library
    // pairs its active muted fills with a matching `-active` foreground because the resting one
    // over those fills measures 3.9-4.4:1 and misses AA.
    '[&:not([data-disabled]):not(:active):hover]:bg-[var(--ds-btn-bg-hover)]',
    '[&:not([data-disabled]):not(:active):hover]:text-[var(--ds-btn-fg-hover)]',
    '[&:not([data-disabled]):active]:bg-[var(--ds-btn-bg-pressed)]',
    '[&:not([data-disabled]):active]:text-[var(--ds-btn-fg-pressed)]',

    // Base UI sets `data-disabled` for both a native `disabled` and the `focusableWhenDisabled`
    // (aria-disabled) case, so keying off it covers both.
    '[&[data-disabled]]:cursor-not-allowed',
    '[&[data-disabled]]:bg-[var(--ds-btn-bg-disabled)]',
    '[&[data-disabled]]:text-[var(--ds-btn-fg-disabled)]',
    '[&[data-disabled]]:border-[var(--ds-btn-border-disabled)]',

    // The Figma matrix has NO focus state. This is added for WCAG 2.4.7, using the focus treatment
    // already specified in Natter's DESIGN.md (2px outline, 2px offset). The colour comes from the
    // focus role, so it inverts with the theme like every other colour in this list — which is why
    // nothing in this recipe names a palette entry any more.
    //
    // It stays an OUTLINE rather than becoming the composed ring the token layer documents — a
    // border plus the two `--effect-focus-*` shadows, laid out in `styles/FocusRing.stories`. No
    // component has shipped that ring yet, so adopting it here would be authoring it for the first
    // time on the control with the thinnest focus coverage in the repo. Deferred on purpose.
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-focus)]',
  ],
  {
    variants: {
      size: {
        /**
         * No type size, unlike every other cell: `2xs` is icon-only, so there is no label to set one
         * for, and declaring one would imply a text form that `IconButtonSize` does not admit.
         *
         * A 20px glyph in a 24px box leaves 2px around it — tighter than the rest of the scale
         * (`xs` is 16-in-32) and deliberately so. The × and the severity icon at the other end of an
         * alert are both 20px, which is what keeps the two ends of the panel balanced.
         */
        '2xs': '[--ds-btn-icon-size:20px]',
        xs: 'text-label-md [--ds-btn-icon-size:16px]',
        small: 'text-label-lg [--ds-btn-icon-size:18px]',
        regular: 'text-label-lg [--ds-btn-icon-size:20px]',
        large: 'text-label-lg [--ds-btn-icon-size:20px]',
      },
      /** Figma's `Style=Icon` — a square button with a single centred icon and no label. */
      iconOnly: {
        true: 'gap-0 p-0',
        false: 'gap-1',
      },
      /**
       * Busy. NOT in the Figma matrix, which has no loading cell (flagged to design, HNS-263) — the
       * treatment below is built from existing tokens rather than invented colours, so it inherits
       * every appearance and both themes for free.
       *
       * A loading button is inert, so it carries `data-disabled` and would otherwise pick up the
       * washed-out disabled tokens. Remapping each disabled slot back onto its normal counterpart
       * keeps the button looking like ITSELF while busy: "working", not "switched off". This is
       * exactly what the slot indirection is for — no new colour is introduced.
       *
       * The opacity slot is the exception: it goes to 0, not 1, because it is the ONLY thing that
       * decides whether the label shows. It feeds the one opacity rule that reaches the content
       * wrapper (`buttonContentRecipe` below), and while busy the label is meant to be invisible
       * behind the spinner — so 1 was both pointless and the reason the label stayed on screen.
       */
      loading: {
        true: [
          '[--ds-btn-bg-disabled:var(--ds-btn-bg)]',
          '[--ds-btn-fg-disabled:var(--ds-btn-fg)]',
          '[--ds-btn-border-disabled:var(--ds-btn-border)]',
          '[--ds-btn-disabled-opacity:0]',
          '[&[data-disabled]]:cursor-progress',
        ],
        false: '',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      appearance: {
        primary: '',
        secondary: '',
        success: '',
        destructive: '',
        muted: '',
      },
      fill: {
        default: '',
        outline: '',
        ghost: '',
        subtle: '',
      },
    },
    compoundVariants: [
      // Height + horizontal padding. Icon-only is square, so it takes no padding.
      { size: 'xs', iconOnly: false, class: 'h-8 px-4' },
      { size: 'small', iconOnly: false, class: 'h-9 px-5' },
      { size: 'regular', iconOnly: false, class: 'h-10 px-5' },
      { size: 'large', iconOnly: false, class: 'h-11 px-5' },
      // `2xs` has no `iconOnly: false` counterpart on purpose — see the scale note above.
      { size: '2xs', iconOnly: true, class: 'size-6' },
      { size: 'xs', iconOnly: true, class: 'size-8' },
      { size: 'small', iconOnly: true, class: 'size-9' },
      { size: 'regular', iconOnly: true, class: 'size-10' },
      { size: 'large', iconOnly: true, class: 'size-11' },

      // Appearance x fill -> the slot-setting class. `ghost` is muted-only in the design and
      // `subtle` primary- and destructive-only, both enforced by the Props type; there is
      // intentionally no primary/secondary/success/destructive ghost, and no subtle for anything
      // outside those two.
      //
      // `muted` has no `default` either: a solid neutral fill is what `secondary / default` already
      // draws, so the cell duplicated it and no product path reached for it. The Props type is what
      // keeps it unreachable — a pair with no class here sets no slot at all, so it would render
      // with no background, label colour or border rather than failing.
      { appearance: 'primary', fill: 'default', class: 'ds-btn-primary-default' },
      { appearance: 'primary', fill: 'outline', class: 'ds-btn-primary-outline' },
      { appearance: 'primary', fill: 'subtle', class: 'ds-btn-primary-subtle' },
      { appearance: 'secondary', fill: 'default', class: 'ds-btn-secondary-default' },
      { appearance: 'secondary', fill: 'outline', class: 'ds-btn-secondary-outline' },
      { appearance: 'success', fill: 'default', class: 'ds-btn-success-default' },
      { appearance: 'success', fill: 'outline', class: 'ds-btn-success-outline' },
      { appearance: 'destructive', fill: 'default', class: 'ds-btn-destructive-default' },
      { appearance: 'destructive', fill: 'outline', class: 'ds-btn-destructive-outline' },
      { appearance: 'destructive', fill: 'subtle', class: 'ds-btn-destructive-subtle' },
      { appearance: 'muted', fill: 'outline', class: 'ds-btn-muted-outline' },
      { appearance: 'muted', fill: 'ghost', class: 'ds-btn-muted-ghost' },
    ],
    defaultVariants: {
      appearance: 'primary',
      fill: 'default',
      size: 'regular',
      iconOnly: false,
      fullWidth: false,
      loading: false,
    },
  }
);

/**
 * Wrapper around the label and icons.
 *
 * This element exists because the disabled treatment for `default` and `ghost` fills is an opacity
 * on the CONTENT only — the background keeps its full-strength disabled token. Applying the opacity
 * to the button itself would wash the background out too. `outline` fills set the slot to 1, so the
 * same markup covers all three fills.
 */
export const buttonContentRecipe = cva(
  [
    'inline-flex items-center justify-center gap-[inherit]',
    'group-data-[disabled]/btn:opacity-[var(--ds-btn-disabled-opacity)]',
  ],
  {
    variants: {
      /**
       * Hidden with `opacity-0`, NOT unmounted, and deliberately so:
       *
       *   - the label keeps reserving its width, so the button does not shrink the moment it starts
       *     working. A button that collapses mid-submit moves everything beside it.
       *   - the accessible name survives. Removing the text would leave a screen-reader user on an
       *     unnamed button precisely when something is happening. `visibility`/`display` would take
       *     the name with them, which is why this stays an opacity.
       *
       * `opacity-0` alone does NOT hide it. The base list above sets opacity through a
       * `group-data-[disabled]` variant, and a loading button always carries `data-disabled`
       * (`disabled={disabled || loading}`), so that rule always applies — at (0,2,0) it outranks
       * this (0,1,0) utility no matter how the utilities sort. The actual hide therefore comes from
       * `buttonRecipe`'s loading variant driving `--ds-btn-disabled-opacity` to 0; this line is what
       * makes the intent local and readable, and covers a `ButtonContent` rendered busy without
       * `data-disabled`. Both resolve to 0, so they cannot disagree.
       */
      loading: { true: 'opacity-0', false: '' },
    },
    defaultVariants: { loading: false },
  }
);

/**
 * The busy indicator, centred over the hidden label.
 *
 * A bordered circle with one transparent edge rather than an SVG or a new token: `border-current`
 * resolves to `--ds-btn-fg`, so it is automatically the right colour in every appearance, both
 * fills and both themes. Sized off `--ds-btn-icon-size` so it tracks the size scale.
 */
export const buttonSpinnerRecipe = cva([
  'pointer-events-none absolute inset-0 m-auto',
  'size-[var(--ds-btn-icon-size)] rounded-full',
  'border-2 border-current border-t-transparent',
  'animate-spin motion-reduce:animate-none',
]);

/** Sizes any icon passed via `startIcon` / `endIcon` / `children` of an icon-only button. */
export const buttonIconRecipe = cva([
  'inline-flex shrink-0 items-center justify-center',
  'size-[var(--ds-btn-icon-size)]',
  '[&_svg]:size-full',
]);
